/**
 * Client-side chart data preprocessing
 * Processes telemetry data on the client device to distribute CPU load
 */

import {
  PreprocessedLapData,
  ChartDataCache,
  SpeedCurveData,
  DistanceRecalibrationMap,
} from '../types/TelemetryTypes'
import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import {
  createMultiResolution,
  createLegacyIndexedTelemetry,
} from './telemetryProcessor'
import { calculatePointDistance } from '../utils/distanceUtils'
import { formatElapsedTime } from '../utils/timeUtils'

/**
 * Configuration options for chart data processing
 */
export interface ProcessingOptions {
  /** Enable multi-resolution generation */
  enableMultiResolution?: boolean
  /** Generate speed curves */
  generateSpeedCurves?: boolean
  /** Generate recalibration maps */
  generateRecalibrationMaps?: boolean
  /** High resolution interval in meters */
  highResInterval?: number
  /** Medium resolution interval in meters */
  mediumResInterval?: number
  /** Low resolution interval in meters */
  lowResInterval?: number
}

const DEFAULT_OPTIONS: ProcessingOptions = {
  enableMultiResolution: true,
  generateSpeedCurves: true,
  generateRecalibrationMaps: true,
  highResInterval: 10,
  mediumResInterval: 50,
  lowResInterval: 100,
}

/**
 * Main chart data processor
 * Processes telemetry data on the client side
 */
export class ChartDataProcessor {
  private options: ProcessingOptions

  constructor(options: Partial<ProcessingOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options }
  }

  /**
   * Process a complete track session
   * @param trackSessionId Track session identifier
   * @param trackId Track identifier
   * @param laps Map of lap data with telemetry
   * @param onProgress Optional progress callback
   * @returns Processed chart data cache
   */
  async processTrackSession(
    trackSessionId: string,
    trackId: string,
    laps: Map<string, { lapData: ProcessorLapInput; telemetry: GpsPoint[] }>,
    onProgress?: (progress: number, message: string) => void
  ): Promise<ChartDataCache> {
    const totalSteps = laps.size * 3 + 1 // Process each lap + generate maps
    let currentStep = 0

    const reportProgress = (message: string) => {
      currentStep++
      if (onProgress) {
        onProgress((currentStep / totalSteps) * 100, message)
      }
    }

    const chartDataCache: ChartDataCache = {
      trackSessionId,
      trackId,
      generatedAt: new Date(),
      version: '1.0.0',
      laps: {},
    }

    // Process each lap
    for (const [lapId, { lapData, telemetry }] of laps) {
      reportProgress(`Processing lap ${lapData.lapNumber}...`)

      const processedLap = await this.processLap(
        lapId,
        lapData,
        telemetry,
        (msg) => reportProgress(`Lap ${lapData.lapNumber}: ${msg}`)
      )

      chartDataCache.laps[lapId] = processedLap
    }

    // Generate recalibration maps if multiple laps
    if (this.options.generateRecalibrationMaps && laps.size > 1) {
      reportProgress('Generating recalibration maps...')
      chartDataCache.recalibrationMaps = this.generateRecalibrationMaps(
        Object.values(chartDataCache.laps)
      )
    }

    return chartDataCache
  }

  /**
   * Process a single lap
   */
  async processLap(
    lapId: string,
    lapData: ProcessorLapInput,
    telemetry: GpsPoint[],
    onProgress?: (message: string) => void
  ): Promise<PreprocessedLapData> {
    if (telemetry.length === 0) {
      throw new Error(`No telemetry data for lap ${lapId}`)
    }

    onProgress?.('Creating multi-resolution data...')
    const multiRes = this.options.enableMultiResolution
      ? createMultiResolution(telemetry)
      : {
          high: {
            resolution: 'high' as const,
            interval: 5,
            data: createLegacyIndexedTelemetry(telemetry),
          },
          medium: {
            resolution: 'medium' as const,
            interval: 25,
            data: createLegacyIndexedTelemetry(telemetry),
          },
          low: {
            resolution: 'low' as const,
            interval: 100,
            data: createLegacyIndexedTelemetry(telemetry),
          },
        }

    onProgress?.('Generating speed curves...')
    const speedCurve = this.options.generateSpeedCurves
      ? this.generateSpeedCurve(telemetry)
      : undefined

    const processedLap: PreprocessedLapData = {
      lapId,
      lapNumber: lapData.lapNumber,
      lapTimeMs: lapData.lapTimeMs,
      startTime: lapData.startTime,
      finishTime: lapData.finishTime,
      telemetry: multiRes,
      lapStartDistance: telemetry[0].distanceMeters,
      lapFinishDistance: telemetry[telemetry.length - 1].distanceMeters,
      speedCurve,
    }

    return processedLap
  }

  /**
   * Generate speed curve data for charts
   */
  private generateSpeedCurve(telemetry: GpsPoint[]): SpeedCurveData {
    const byDistance = telemetry.map((point) => ({
      distance: point.distanceMeters,
      speed: point.speedKmh,
      time: point.timeString ?? formatElapsedTime(point.elapsedMs),
    }))

    const byTime = telemetry.map((point) => ({
      time: point.timeString ?? formatElapsedTime(point.elapsedMs),
      timeMs: point.elapsedMs,
      speed: point.speedKmh,
      distance: point.distanceMeters,
    }))

    return { byDistance, byTime }
  }

  /**
   * Generate distance recalibration maps for lap comparisons
   */
  private generateRecalibrationMaps(
    laps: PreprocessedLapData[]
  ): Record<string, DistanceRecalibrationMap> {
    const maps: Record<string, DistanceRecalibrationMap> = {}

    // Find reference lap (shortest total distance)
    const referenceLap = laps.reduce((shortest, current) => {
      const shortestDist =
        shortest.lapFinishDistance - shortest.lapStartDistance
      const currentDist = current.lapFinishDistance - current.lapStartDistance
      return currentDist < shortestDist ? current : shortest
    })

    // Create recalibration map for each non-reference lap
    for (const targetLap of laps) {
      if (targetLap.lapId === referenceLap.lapId) continue

      const refPoints = referenceLap.telemetry.high.data.points
      const targetPoints = targetLap.telemetry.high.data.points

      const map = this.createRecalibrationMap(
        referenceLap.lapId,
        targetLap.lapId,
        refPoints,
        targetPoints
      )

      maps[`${referenceLap.lapId}:${targetLap.lapId}`] = map
    }

    return maps
  }

  /**
   * Create a single recalibration map between two laps
   */
  private createRecalibrationMap(
    referenceLapId: string,
    targetLapId: string,
    refPoints: GpsPoint[],
    targetPoints: GpsPoint[]
  ): DistanceRecalibrationMap {
    const mappings: Record<number, number> = {}

    let lastRefIndex = 0
    const searchWindow = 100

    // Sample every 10 meters
    const maxDistance = refPoints[refPoints.length - 1].distanceMeters
    for (let distance = 0; distance <= maxDistance; distance += 10) {
      // Find closest point in reference lap by distance
      const refPoint = this.findClosestByDistance(
        refPoints,
        distance,
        lastRefIndex,
        searchWindow
      )
      if (!refPoint) continue

      lastRefIndex = refPoint.index

      // Find geographically closest point in target lap
      const targetPoint = this.findClosestByGeo(targetPoints, refPoint.point)
      if (!targetPoint) continue

      mappings[Math.round(distance)] = targetPoint.distanceMeters
    }

    return {
      referenceLapId,
      targetLapId,
      mappings,
    }
  }

  /**
   * Find closest point by distance
   */
  private findClosestByDistance(
    points: GpsPoint[],
    targetDistance: number,
    startIndex: number,
    searchWindow: number
  ): { point: GpsPoint; index: number } | null {
    const start = Math.max(0, startIndex)
    const end = Math.min(points.length, start + searchWindow)

    let closestPoint = points[start]
    let minDiff = Math.abs(points[start].distanceMeters - targetDistance)
    let closestIndex = start

    for (let i = start + 1; i < end; i++) {
      const diff = Math.abs(points[i].distanceMeters - targetDistance)
      if (diff < minDiff) {
        minDiff = diff
        closestPoint = points[i]
        closestIndex = i
      }
    }

    return { point: closestPoint, index: closestIndex }
  }

  /**
   * Find geographically closest point
   */
  private findClosestByGeo(
    points: GpsPoint[],
    target: GpsPoint
  ): GpsPoint | null {
    if (points.length === 0) return null

    let closestPoint = points[0]
    let minDistance = calculatePointDistance(target, closestPoint)

    for (let i = 1; i < points.length; i++) {
      const distance = calculatePointDistance(target, points[i])
      if (distance < minDistance) {
        minDistance = distance
        closestPoint = points[i]
      }
    }

    return closestPoint
  }
}

/**
 * Minimal lap metadata needed for preprocessing
 * Uses structural typing - any object with these properties will work
 */
export type ProcessorLapInput = {
  lapNumber: number
  lapTimeMs: number
  startTime: Date
  finishTime: Date
}
